import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import {
  type AccessPolicyUpdate,
  clearRouteAccessPolicy,
  getRouteAccessPolicy,
  getRouteBackendOverrides,
  getRouteBaseAccessPolicy,
  type RouteBackendOverrides,
  type RouteStatus,
  updateRouteAccessPolicy,
  updateRouteBackendOverrides,
  updateRouteBaseAccessPolicy,
} from "@/api/admin-api"
import { api } from "@/lib/api-client"

import { FAST_STALE_TIME, GC_TIME } from "./_shared"

// Route Management
export function useAdminRoutesQuery(enabled = true) {
  return useQuery({
    queryKey: ["admin", "routes"],
    queryFn: async (): Promise<RouteStatus[]> => {
      const response = await api.get("/admin/routes")
      const data = response.data || response
      // Ensure we always return an array
      return Array.isArray(data) ? data : []
    },
    enabled,
    staleTime: FAST_STALE_TIME,
    gcTime: GC_TIME,
  })
}

export function useUpdateRouteStatusMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ routeId, enabled }: { routeId: string; enabled: boolean }) => {
      const response = await api.put(`/admin/routes/${routeId}/status`, { enabled })
      const data = response.data || response
      if (data.success === false) {
        throw new Error(data.message || "Failed to update route status")
      }
      return data
    },
    onMutate: async ({ routeId, enabled }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["admin", "routes"] })

      // Snapshot the previous value
      const previousRoutes = queryClient.getQueryData<Array<{ id: string; enabled: boolean }>>([
        "admin",
        "routes",
      ])

      // Optimistically update to the new value
      if (previousRoutes) {
        queryClient.setQueryData(
          ["admin", "routes"],
          previousRoutes.map((route) => (route.id === routeId ? { ...route, enabled } : route))
        )
      }

      // Return a context object with the snapshotted value
      return { previousRoutes }
    },
    onError: (err, variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousRoutes) {
        queryClient.setQueryData(["admin", "routes"], context.previousRoutes)
      }
      // Refetch on error to ensure consistency
      void queryClient.invalidateQueries({ queryKey: ["admin", "routes"] })
    },
    // Don't refetch on success - we already updated optimistically
  })
}

export function useRouteBackendOverridesQuery(routeId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ["admin", "routes", routeId, "backend-overrides"],
    queryFn: () => getRouteBackendOverrides(routeId ?? ""),
    enabled: enabled && !!routeId,
    staleTime: FAST_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
  })
}

export function useUpdateRouteBackendOverridesMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      routeId,
      overrides,
    }: {
      routeId: string
      overrides: RouteBackendOverrides
    }) => {
      return await updateRouteBackendOverrides(routeId, overrides)
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "routes"] })
      void queryClient.invalidateQueries({
        queryKey: ["admin", "routes", variables.routeId, "backend-overrides"],
      })
    },
  })
}

export function useAdminRouteAccessPolicyQuery(routeId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ["admin", "routes", routeId, "access"],
    queryFn: () => getRouteAccessPolicy(routeId ?? ""),
    enabled: enabled && !!routeId,
    staleTime: FAST_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
  })
}

export function useUpdateRouteAccessPolicyMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ routeId, policy }: { routeId: string; policy: AccessPolicyUpdate }) => {
      return await updateRouteAccessPolicy(routeId, policy)
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "routes"] })
      void queryClient.invalidateQueries({
        queryKey: ["admin", "routes", variables.routeId, "access"],
      })
    },
  })
}

export function useAdminRouteBaseAccessPolicyQuery(enabled = true) {
  return useQuery({
    queryKey: ["admin", "routes", "base", "access"],
    queryFn: () => getRouteBaseAccessPolicy(),
    enabled,
    staleTime: FAST_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
  })
}

export function useUpdateRouteBaseAccessPolicyMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ policy }: { policy: AccessPolicyUpdate }) => {
      return await updateRouteBaseAccessPolicy(policy)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "routes", "base", "access"] })
      void queryClient.invalidateQueries({ queryKey: ["admin", "routes"] })
    },
  })
}

export function useClearRouteAccessPolicyMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ routeId }: { routeId: string }) => {
      return await clearRouteAccessPolicy(routeId)
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "routes"] })
      void queryClient.invalidateQueries({
        queryKey: ["admin", "routes", variables.routeId, "access"],
      })
    },
  })
}
